import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, authFetch } from '../../context/AuthContext';

/* ── 타입 ── */
interface Product {
  id: number; code: string; name: string; category: string;
  price: number; min_stock: number; stock: number; is_low: number;
  image_url: string | null; is_active: number; description?: string;
  sold_out?: number;
}

const ALL_CATEGORIES = ['내부판매용','핸드백','숄더백','크로스백','백팩','여행용 가방','OEM/ODM','기타'];
const blankForm = { code:'', name:'', category:'내부판매용', price:'', min_stock:'10', description:'' };

/* ── 공통 ── */
const inp = (v: string, fn: (v: string) => void, p: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
  <input value={v} onChange={e => fn(e.target.value)} {...p}
    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
);

const Modal = ({ title, wide, onClose, children }: { title: string; wide?: boolean; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 py-6">
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
      <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-stone-100 z-10">
        <h2 className="font-bold text-stone-900">{title}</h2>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-2xl leading-none">×</button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

/* ══════════════════════════════════════════ */
export default function ProductCatalogPage() {
  const { user } = useAuth();
  const isAdmin   = user?.role === 'admin';

  const [products,  setProducts]  = useState<Product[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [view,      setView]      = useState<'grid' | 'table'>('grid');
  const [catFilter, setCatFilter] = useState('전체');
  const [search,    setSearch]    = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [modal,   setModal]   = useState<'add' | 'edit' | 'delete' | 'image' | null>(null);
  const [target,  setTarget]  = useState<Product | null>(null);
  const [form,    setForm]    = useState(blankForm);
  const [saving,  setSaving]  = useState(false);
  const [errMsg,  setErrMsg]  = useState('');
  const [toast,   setToast]   = useState('');

  /* 이미지 업로드 상태 */
  const [imgPreview,  setImgPreview]  = useState<string | null>(null);
  const [imgFile,     setImgFile]     = useState<File | null>(null);
  const [imgUploading, setImgUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };
  const close  = () => { setModal(null); setTarget(null); setErrMsg(''); setImgPreview(null); setImgFile(null); };

  /* ── 제품 로드 ── */
  const load = useCallback(async () => {
    setLoading(true);
    // is_active 모두 조회 (관리자)
    const res = await authFetch('/api/inventory');
    if (res.ok) {
      const data = await res.json();
      // 비활성 포함 전체 조회를 위해 products API 사용
      const allRes = await authFetch('/api/products');
      if (allRes.ok) {
        const all: any[] = await allRes.json();
        // inventory(재고) 데이터와 병합
        const stockMap: Record<number, number> = {};
        data.forEach((p: Product) => { stockMap[p.id] = p.stock; });
        setProducts(all.map(p => ({ ...p, stock: stockMap[p.id] ?? 0, is_low: 0 })));
      } else {
        setProducts(data);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── 필터링 ── */
  const filtered = products.filter(p => {
    const catOk    = catFilter === '전체' || p.category === catFilter;
    const searchOk = p.name.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase());
    const activeOk = showInactive ? true : p.is_active === 1;
    return catOk && searchOk && activeOk;
  });

  /* ── 모달 오픈 ── */
  const openAdd = () => { setForm(blankForm); setErrMsg(''); setModal('add'); };
  const openEdit = (p: Product) => {
    setTarget(p);
    setForm({ code: p.code, name: p.name, category: p.category,
              price: String(p.price), min_stock: String(p.min_stock), description: p.description ?? '' });
    setErrMsg('');
    setModal('edit');
  };
  const openImage  = (p: Product) => { setTarget(p); setImgPreview(p.image_url); setImgFile(null); setModal('image'); };
  const openDelete = (p: Product) => { setTarget(p); setModal('delete'); };

  /* ── 제품 추가 ── */
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setErrMsg('');
    const res  = await authFetch('/api/products', {
      method: 'POST',
      body: JSON.stringify({ ...form, price: Number(form.price), min_stock: Number(form.min_stock) }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '추가 실패'); return; }
    await load();
    flash(`"${data.name}" 제품이 추가되었습니다.`);
    close();
  };

  /* ── 제품 수정 ── */
  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true); setErrMsg('');
    const res  = await authFetch(`/api/products/${target.id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...form, price: Number(form.price), min_stock: Number(form.min_stock) }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) { setErrMsg(data.error ?? '수정 실패'); return; }
    await load();
    flash('제품 정보가 수정되었습니다.');
    close();
  };

  /* ── 활성/비활성 토글 ── */
  const toggleActive = async (p: Product) => {
    await authFetch(`/api/products/${p.id}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: p.is_active ? 0 : 1 }),
    });
    await load();
    flash(`"${p.name}" ${p.is_active ? '비공개' : '공개'} 처리되었습니다.`);
  };

  /* ── 삭제 ── */
  const handleDelete = async () => {
    if (!target) return;
    setSaving(true);
    const res  = await authFetch(`/api/products/${target.id}`, { method: 'DELETE' });
    const data = await res.json();
    setSaving(false);
    await load();
    flash(data.note ?? '처리 완료');
    close();
  };

  /* ── 이미지 선택 ── */
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  /* ── 이미지 업로드 ── */
  const handleImageUpload = async () => {
    if (!target || !imgFile) return;
    setImgUploading(true);
    const fd = new FormData();
    fd.append('image', imgFile);
    const token = localStorage.getItem('cobrand_token');
    const res   = await fetch(`/api/products/${target.id}/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token ?? ''}` },
      body: fd,
    });
    const data = await res.json();
    setImgUploading(false);
    if (!res.ok) { setErrMsg(data.error ?? '업로드 실패'); return; }
    await load();
    flash('이미지가 업로드되었습니다.');
    close();
  };

  /* ── 이미지 삭제 ── */
  const handleImageDelete = async () => {
    if (!target) return;
    setImgUploading(true);
    await authFetch(`/api/products/${target.id}/image`, { method: 'DELETE' });
    setImgUploading(false);
    await load();
    flash('이미지가 삭제되었습니다.');
    close();
  };

  const categories = ['전체', ...ALL_CATEGORIES];

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {toast && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          {toast}
        </div>
      )}

      {/* 헤더 */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">제품 카탈로그 관리</h1>
          <p className="text-stone-500 text-sm mt-1">
            공개 웹사이트 제품 등록·수정·이미지 관리&nbsp;·&nbsp;
            <a href="/catalog" target="_blank" rel="noreferrer" className="text-amber-600 hover:underline text-xs">
              공개 사이트 미리보기 ↗
            </a>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* 보기 전환 */}
          <div className="flex border border-stone-200 rounded-lg overflow-hidden">
            <button onClick={() => setView('grid')} className={`px-3 py-2 text-sm transition ${view==='grid' ? 'bg-stone-800 text-white' : 'hover:bg-stone-50 text-stone-600'}`}>
              ⊞ 그리드
            </button>
            <button onClick={() => setView('table')} className={`px-3 py-2 text-sm transition ${view==='table' ? 'bg-stone-800 text-white' : 'hover:bg-stone-50 text-stone-600'}`}>
              ☰ 테이블
            </button>
          </div>
          {isAdmin && (
            <button onClick={openAdd}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition">
              + 제품 추가
            </button>
          )}
        </div>
      </div>

      {/* 검색 & 필터 */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-5 flex flex-wrap gap-3 items-center">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="제품명 또는 코드 검색..."
          className="border border-stone-200 rounded-lg px-3 py-2 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <div className="flex flex-wrap gap-1.5">
          {categories.map(cat => (
            <button key={cat} onClick={() => setCatFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                catFilter === cat ? 'bg-amber-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              } ${cat === '내부판매용' ? 'border border-amber-300' : ''}`}>
              {cat === '내부판매용' && ''}{cat}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-stone-500 cursor-pointer ml-2">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
            className="accent-amber-500" />
          비공개 포함
        </label>
      </div>

      {/* 요약 */}
      <div className="flex gap-4 text-xs text-stone-500 mb-5 px-1">
        <span>전체 {products.length}종</span>
        <span className="text-green-600">공개 {products.filter(p=>p.is_active).length}종</span>
        <span className="text-stone-400">비공개 {products.filter(p=>!p.is_active).length}종</span>
        <span className="text-amber-600">내부판매용 {products.filter(p=>p.category==='내부판매용').length}종</span>
      </div>

      {loading ? (
        <div className="text-center py-24 text-stone-400 animate-pulse">제품 로드 중...</div>
      ) : view === 'grid' ? (

        /* ── 그리드 뷰 ── */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filtered.map(p => {
            const soldOut = p.stock <= 0;
            return (
              <div key={p.id}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden border-2 transition hover:shadow-md ${
                  !p.is_active ? 'border-stone-200 opacity-50' :
                  p.category === '내부판매용' ? 'border-amber-300' : 'border-transparent'
                }`}>

                {/* 이미지 */}
                <div className="relative bg-stone-100 h-36 flex items-center justify-center overflow-hidden group">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl">이미지 없음</span>
                  )}

                  {/* SOLD OUT */}
                  {p.category === '내부판매용' && soldOut && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-xs font-bold tracking-widest">SOLD OUT</span>
                    </div>
                  )}

                  {/* 비공개 */}
                  {!p.is_active && (
                    <div className="absolute inset-0 bg-stone-400/40 flex items-center justify-center">
                      <span className="bg-stone-800 text-white text-xs font-bold px-2 py-0.5 rounded">비공개</span>
                    </div>
                  )}

                  {/* 이미지 관리 버튼 */}
                  {isAdmin && (
                    <button onClick={() => openImage(p)}
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="bg-white text-stone-800 text-xs font-bold px-3 py-1.5 rounded-full shadow">
                        📷 이미지
                      </span>
                    </button>
                  )}
                </div>

                {/* 정보 */}
                <div className="p-3">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <p className="text-xs font-semibold text-stone-800 line-clamp-2 flex-1">{p.name}</p>
                    {p.category === '내부판매용' && (
                      <span className="text-amber-500 text-xs flex-shrink-0"></span>
                    )}
                  </div>
                  <p className="text-xs text-stone-400 mb-1">{p.category}</p>
                  <p className="text-sm font-bold text-amber-600 mb-2">{p.price.toLocaleString()}원</p>

                  {/* 재고 */}
                  {p.category === '내부판매용' && (
                    <div className={`text-xs font-semibold px-2 py-0.5 rounded-full text-center mb-2 ${
                      soldOut ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {soldOut ? 'SOLD OUT' : `재고 ${p.stock}개`}
                    </div>
                  )}

                  {/* 관리 버튼 */}
                  {isAdmin && (
                    <div className="space-y-1 mt-1">
                      <Link to={`/admin/catalog/${p.id}/edit`}
                        className="block w-full text-center text-xs py-1.5 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg transition">
                        상세페이지 편집
                      </Link>
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(p)}
                          className="flex-1 text-xs py-1.5 bg-stone-100 hover:bg-blue-100 text-stone-600 hover:text-blue-700 rounded-lg transition font-medium">
                          수정
                        </button>
                        <button onClick={() => toggleActive(p)}
                          className={`flex-1 text-xs py-1.5 rounded-lg transition font-medium ${
                            p.is_active ? 'bg-orange-50 hover:bg-orange-100 text-orange-600' : 'bg-green-50 hover:bg-green-100 text-green-600'
                          }`}>
                          {p.is_active ? '비공개' : '공개'}
                        </button>
                        <button onClick={() => openDelete(p)}
                          className="px-2 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 rounded-lg transition">
                          🗑
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* 제품 추가 버튼 (그리드 끝) */}
          {isAdmin && (
            <button onClick={openAdd}
              className="border-2 border-dashed border-stone-200 rounded-2xl h-full min-h-[240px] flex flex-col items-center justify-center gap-2 text-stone-400 hover:border-amber-400 hover:text-amber-500 transition">
              <span className="text-3xl">+</span>
              <span className="text-xs font-semibold">제품 추가</span>
            </button>
          )}
        </div>

      ) : (

        /* ── 테이블 뷰 ── */
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 w-16">이미지</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">제품명</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">카테고리</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">단가</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">재고</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">공개</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">관리</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const soldOut = p.stock <= 0;
                return (
                  <tr key={p.id} className={`border-b border-stone-50 hover:bg-stone-50 transition ${!p.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="w-12 h-12 bg-stone-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer"
                        onClick={() => isAdmin && openImage(p)}>
                        {p.image_url
                          ? <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                          : <span className="text-xl">이미지 없음</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-stone-800">{p.name}</p>
                      <p className="text-xs text-stone-400 font-mono">{p.code}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        p.category === '내부판매용' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {p.category === '내부판매용' && ''}{p.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-700">{p.price.toLocaleString()}원</td>
                    <td className="px-4 py-3 text-right">
                      {p.category === '내부판매용'
                        ? <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${soldOut ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {soldOut ? 'SOLD OUT' : `${p.stock}개`}
                          </span>
                        : <span className="text-stone-600">{p.stock}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => isAdmin && toggleActive(p)} disabled={!isAdmin}
                        className={`w-10 h-6 rounded-full transition relative ${p.is_active ? 'bg-green-500' : 'bg-stone-300'} ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${p.is_active ? 'left-4.5 translate-x-0' : 'left-0.5'}`}
                          style={{ left: p.is_active ? '18px' : '2px' }} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                        <div className="flex items-center justify-center gap-1">
                          <Link to={`/admin/catalog/${p.id}/edit`} title="상세페이지 편집"
                            className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition text-base font-bold text-xs px-2">
                            편집
                          </Link>
                          <button onClick={() => openImage(p)} title="이미지" className="p-1.5 rounded hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition text-base">📷</button>
                          <button onClick={() => openEdit(p)} title="수정" className="p-1.5 rounded hover:bg-amber-50 text-stone-400 hover:text-amber-600 transition">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                          </button>
                          <button onClick={() => openDelete(p)} title="삭제" className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="text-center py-16 text-stone-400">제품이 없습니다.</div>}
        </div>
      )}

      {/* ── 모달: 제품 추가 ── */}
      {modal === 'add' && (
        <Modal title="제품 추가" onClose={close}>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">코드 *</label>{inp(form.code, v=>setForm({...form,code:v}), {placeholder:'BAG-001', required:true})}</div>
              <div>
                <label className="text-xs font-semibold text-stone-500 block mb-1">카테고리 *</label>
                <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} required
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  {ALL_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div><label className="text-xs font-semibold text-stone-500 block mb-1">제품명 *</label>{inp(form.name,v=>setForm({...form,name:v}),{placeholder:'클래식 토트백 (블랙)',required:true})}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">단가 (원) *</label>{inp(form.price,v=>setForm({...form,price:v}),{type:'number',placeholder:'185000',required:true,min:'0'})}</div>
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">최소 재고</label>{inp(form.min_stock,v=>setForm({...form,min_stock:v}),{type:'number',placeholder:'10',min:'0'})}</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 block mb-1">제품 설명</label>
              <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={2}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                placeholder="제품 상세 설명 (선택)" />
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm">
                {saving ? '저장 중...' : '추가'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 제품 수정 ── */}
      {modal === 'edit' && target && (
        <Modal title={`제품 수정 — ${target.name}`} onClose={close}>
          <form onSubmit={handleEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">코드</label>{inp(form.code,v=>setForm({...form,code:v}))}</div>
              <div>
                <label className="text-xs font-semibold text-stone-500 block mb-1">카테고리</label>
                <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  {ALL_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div><label className="text-xs font-semibold text-stone-500 block mb-1">제품명</label>{inp(form.name,v=>setForm({...form,name:v}))}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">단가 (원)</label>{inp(form.price,v=>setForm({...form,price:v}),{type:'number',min:'0'})}</div>
              <div><label className="text-xs font-semibold text-stone-500 block mb-1">최소 재고</label>{inp(form.min_stock,v=>setForm({...form,min_stock:v}),{type:'number',min:'0'})}</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 block mb-1">제품 설명</label>
              <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={2}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                placeholder="제품 상세 설명 (선택)" />
            </div>
            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm">
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 모달: 이미지 관리 ── */}
      {modal === 'image' && target && (
        <Modal title={`이미지 관리 — ${target.name}`} wide onClose={close}>
          <div className="space-y-5">
            {/* 현재 이미지 미리보기 */}
            <div>
              <p className="text-xs font-semibold text-stone-500 mb-2">
                {imgFile ? '새 이미지 미리보기' : '현재 이미지'}
              </p>
              <div className="relative bg-stone-100 rounded-2xl overflow-hidden h-52 flex items-center justify-center">
                {imgPreview ? (
                  <img src={imgPreview} alt="" className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center text-stone-400">
                    <span className="text-5xl block mb-2">📷</span>
                    <span className="text-sm">이미지 없음</span>
                  </div>
                )}
                {imgFile && (
                  <span className="absolute top-2 right-2 bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded">
                    새 이미지
                  </span>
                )}
              </div>
            </div>

            {/* 파일 선택 */}
            <div>
              <p className="text-xs font-semibold text-stone-500 mb-2">이미지 업로드 (JPG, PNG, WEBP · 5MB 이하)</p>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
              <button type="button" onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-stone-300 hover:border-amber-400 rounded-xl py-4 text-sm text-stone-500 hover:text-amber-600 transition flex items-center justify-center gap-2">
                <span className="text-xl">📁</span> 파일 선택
              </button>
            </div>

            {errMsg && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{errMsg}</p>}

            <div className="flex gap-2">
              {target.image_url && (
                <button type="button" onClick={handleImageDelete} disabled={imgUploading}
                  className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-2.5 rounded-xl text-sm transition disabled:opacity-50">
                  이미지 삭제
                </button>
              )}
              <button type="button" onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-xl text-sm hover:bg-stone-50">취소</button>
              <button type="button" onClick={handleImageUpload} disabled={!imgFile || imgUploading}
                className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold py-2.5 rounded-xl text-sm transition">
                {imgUploading ? '업로드 중...' : '업로드'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── 모달: 삭제 ── */}
      {modal === 'delete' && target && (
        <Modal title="제품 삭제" onClose={close}>
          <div className="text-center py-2">
            <div className="text-5xl mb-4">오류</div>
            <p className="font-semibold text-stone-800 mb-1">{target.name}</p>
            <p className="text-stone-500 text-sm mb-6">이력이 있으면 비공개 처리됩니다.</p>
            {errMsg && <p className="text-red-500 text-xs mb-3">{errMsg}</p>}
            <div className="flex gap-3">
              <button onClick={close} className="flex-1 border border-stone-200 py-2.5 rounded-lg text-sm hover:bg-stone-50">취소</button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-sm">
                {saving ? '처리 중...' : '삭제'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
